import React, { useState } from 'react';

// Utility function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

interface DistributorRequestProps {
  showModal: boolean;
  requestingDistributor: any | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const DistributorRequest: React.FC<DistributorRequestProps> = ({
  showModal,
  requestingDistributor,
  onClose,
  onSuccess
}) => {
  const [requestMedicines, setRequestMedicines] = useState<any[]>([{
    medicine_name: '',
    medicine_company: '',
    medicine_composition: '',
    quantity: '',
    preferred_brand: '',
    notes: ''
  }]);
  const [extraFields, setExtraFields] = useState<any[]>([]);
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [requestUrgency, setRequestUrgency] = useState('normal');
  const [requestNotes, setRequestNotes] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle window resize for mobile detection
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (showModal && requestingDistributor) {
      setRequestMedicines([{
        medicine_name: '',
        medicine_company: '',
        medicine_composition: '',
        quantity: '',
        preferred_brand: '',
        notes: ''
      }]);
      setExtraFields([]);
      setRequestError('');
      setRequestUrgency('normal');
      setRequestNotes('');
      setIsSubmittingRequest(false);
    }
  }, [showModal, requestingDistributor]);

  // Add new medicine row
  const addMedicineRow = () => {
    setRequestMedicines([...requestMedicines, {
      medicine_name: '',
      medicine_company: '',
      medicine_composition: '',
      quantity: '',
      preferred_brand: '',
      notes: ''
    }]);
  };

  // Remove medicine row
  const removeMedicineRow = (index: number) => {
    if (requestMedicines.length > 1) {
      setRequestMedicines(requestMedicines.filter((_, i) => i !== index));
    }
  };

  // Update medicine field
  const updateMedicineField = (index: number, field: string, value: string) => {
    const updatedMedicines = [...requestMedicines];
    updatedMedicines[index][field] = value;
    setRequestMedicines(updatedMedicines);
  };

  // Add extra field
  const addExtraField = () => {
    setExtraFields([...extraFields, {
      field_name: '',
      field_value: ''
    }]);
  };

  // Remove extra field
  const removeExtraField = (index: number) => {
    setExtraFields(extraFields.filter((_, i) => i !== index));
  };

  // Update extra field
  const updateExtraField = (index: number, field: string, value: string) => {
    const updatedFields = [...extraFields];
    updatedFields[index][field] = value;
    setExtraFields(updatedFields);
  };

  // Close request modal
  const closeRequestModal = () => {
    setRequestMedicines([{
      medicine_name: '',
      medicine_company: '',
      medicine_composition: '',
      quantity: '',
      preferred_brand: '',
      notes: ''
    }]);
    setExtraFields([]);
    setRequestError('');
    setRequestUrgency('normal');
    setRequestNotes('');
    setIsSubmittingRequest(false);
    onClose();
  };

  // Submit request stock
  const submitRequestStock = async () => {
    if (!requestingDistributor) {
      setRequestError('No distributor selected');
      return;
    }

    // Validate medicines
    const validMedicines = requestMedicines.filter(med => 
      med.medicine_name.trim() && med.quantity.trim()
    );

    if (validMedicines.length === 0) {
      setRequestError('Please add at least one medicine with name and quantity');
      return;
    }

    try {
      setIsSubmittingRequest(true);
      setRequestError('');

      const authToken = getAuthToken();
      
      if (!authToken) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      // Prepare request data
      const requestData = {
        distributor_id: requestingDistributor.id,
        urgency: requestUrgency,
        notes: requestNotes.trim(),
        items: validMedicines.map(med => ({
          medicine_name: med.medicine_name.trim(),
          medicine_company: med.medicine_company.trim(),
          medicine_composition: med.medicine_composition.trim(),
          quantity: parseInt(med.quantity) || 0,
          preferred_brand: med.preferred_brand.trim(),
          notes: med.notes.trim()
        }))
      };

      console.log('Submitting request stock:', requestData);

      const response = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/request-medicines', {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(requestData)
      });

      console.log('Request stock response:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to submit request (Status: ${response.status}): ${errorText}`);
      }

      const responseData = await response.json();
      console.log('Request submitted successfully:', responseData);

      alert('Stock request submitted successfully!');
      closeRequestModal();
      
      if (onSuccess) {
        onSuccess();
      }

    } catch (error: any) {
      console.error('Error submitting stock request:', error);
      setRequestError(error.message);
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  if (!showModal || !requestingDistributor) {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '1rem'
    }}>
      <div style={{
        background: 'white',
        borderRadius: isMobile ? '12px' : '16px',
        padding: isMobile ? '1rem' : '2rem',
        maxHeight: isMobile ? '95vh' : '90vh',
        overflow: 'auto',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
        width: '100%',
        maxWidth: isMobile ? '100%' : '900px',
        margin: isMobile ? '0.5rem' : '0',
        boxSizing: 'border-box'
      }}>
        {/* Modal Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <h2 style={{
            fontSize: isMobile ? '1.1rem' : '1.5rem',
            fontWeight: '700',
            color: '#1f2937',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '0.5rem' : '0.75rem',
            flexWrap: 'wrap'
          }}>
            <div style={{
              width: isMobile ? '32px' : '40px',
              height: isMobile ? '32px' : '40px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              borderRadius: isMobile ? '8px' : '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
              flexShrink: 0
            }}>
              <svg width={isMobile ? "16" : "20"} height={isMobile ? "16" : "20"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
              </svg>
            </div>
            <span style={{ fontSize: isMobile ? '0.9rem' : 'inherit' }}>
              {isMobile ? 'Request Stock' : `Request Stock from ${requestingDistributor.name}`}
            </span>
          </h2>
          <button
            onClick={closeRequestModal}
            style={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white',
              border: 'none',
              padding: '0.5rem',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 3px 10px rgba(239, 68, 68, 0.3)',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 3px 10px rgba(239, 68, 68, 0.3)';
            }}
          >
            ×
          </button>
        </div>

        {/* Distributor Info */}
        <div style={{
          background: 'rgba(59, 130, 246, 0.05)',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1.5rem',
          border: '1px solid rgba(59, 130, 246, 0.1)'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: isMobile ? '0.75rem' : '1rem',
            fontSize: isMobile ? '0.85rem' : '0.9rem'
          }}>
            <div>
              <span style={{ fontWeight: '600', color: '#374151' }}>Distributor ID:</span>
              <div style={{ color: '#6b7280' }}>{requestingDistributor.id}</div>
            </div>
            <div>
              <span style={{ fontWeight: '600', color: '#374151' }}>Name:</span>
              <div style={{ color: '#6b7280' }}>{requestingDistributor.name}</div>
            </div>
            <div>
              <span style={{ fontWeight: '600', color: '#374151' }}>Phone:</span>
              <div style={{ color: '#6b7280' }}>{requestingDistributor.phone || 'N/A'}</div>
            </div>
            <div>
              <span style={{ fontWeight: '600', color: '#374151' }}>Email:</span>
              <div style={{ color: '#6b7280' }}>{requestingDistributor.email || 'N/A'}</div>
            </div>
          </div>
        </div>

        {/* Request Details */}
        <div style={{
          background: 'rgba(16, 185, 129, 0.05)',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1.5rem',
          border: '1px solid rgba(16, 185, 129, 0.1)'
        }}>
          <h3 style={{
            fontSize: '1.1rem',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14,2 14,8 20,8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10,9 9,9 8,9"/>
            </svg>
            Request Details
          </h3>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr',
            gap: isMobile ? '0.75rem' : '1rem',
            alignItems: 'end'
          }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.8rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Urgency Level
              </label>
              <select
                value={requestUrgency}
                onChange={(e) => setRequestUrgency(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  backgroundColor: 'white',
                  color: '#374151',
                  outline: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#10b981';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.8rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Notes
              </label>
              <textarea
                value={requestNotes}
                onChange={(e) => setRequestNotes(e.target.value)}
                placeholder="Add any special instructions or notes..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  backgroundColor: 'white',
                  color: '#374151',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                  minHeight: '80px'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#10b981';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>
        </div>

        {/* Error Message */}
        {requestError && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem',
            color: '#dc2626',
            fontSize: '0.9rem'
          }}>
            {requestError}
          </div>
        )}

        {/* Medicines Table */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <h3 style={{
              fontSize: '1.2rem',
              fontWeight: '600',
              color: '#1f2937',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7z"/>
              </svg>
              Medicines ({requestMedicines.length})
            </h3>
            <button
              onClick={addMedicineRow}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 2px 6px rgba(16, 185, 129, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 10px rgba(16, 185, 129, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(16, 185, 129, 0.3)';
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M5 12h14"/>
              </svg>
              Add Medicine
            </button>
          </div>

          {/* Table Header - Hidden on Mobile */}
          {!isMobile && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1.5fr 1fr 1fr 0.8fr 1fr 0.8fr 60px',
              gap: '0.5rem',
              marginBottom: '0.5rem',
              padding: '0.75rem',
              background: 'rgba(59, 130, 246, 0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(59, 130, 246, 0.1)',
              fontSize: '0.8rem',
              fontWeight: '600',
              color: '#374151',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              <div>Medicine Name</div>
              <div>Company</div>
              <div>Composition</div>
              <div>Quantity</div>
              <div>Preferred Brand</div>
              <div>Notes</div>
              <div style={{ textAlign: 'center' }}>Action</div>
            </div>
          )}

          {/* Medicine Rows */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? '0.75rem' : '0.5rem',
            maxHeight: isMobile ? '400px' : '300px',
            overflow: 'auto',
            padding: isMobile ? '0.25rem' : '0.5rem'
          }}>
            {requestMedicines.map((medicine, index) => (
              <div key={index} style={{
                display: isMobile ? 'flex' : 'grid',
                flexDirection: isMobile ? 'column' : 'row',
                gridTemplateColumns: isMobile ? 'none' : '1.5fr 1fr 1fr 0.8fr 1fr 0.8fr 60px',
                gap: isMobile ? '0.75rem' : '0.5rem',
                padding: isMobile ? '1rem' : '0.75rem',
                background: 'white',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.2s ease',
                position: 'relative'
              }}>
                {isMobile ? (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280' }}>Medicine Name</label>
                        <input
                          type="text"
                          placeholder="Medicine name"
                          value={medicine.medicine_name}
                          onChange={(e) => updateMedicineField(index, 'medicine_name', e.target.value)}
                          style={{
                            padding: '0.75rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            outline: 'none',
                            transition: 'border-color 0.3s ease',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                        />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280' }}>Company</label>
                          <input
                            type="text"
                            placeholder="Company"
                            value={medicine.medicine_company}
                            onChange={(e) => updateMedicineField(index, 'medicine_company', e.target.value)}
                            style={{
                              padding: '0.75rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '0.9rem',
                              outline: 'none',
                              transition: 'border-color 0.3s ease',
                              width: '100%',
                              boxSizing: 'border-box'
                            }}
                            onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                            onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                          />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280' }}>Quantity</label>
                          <input
                            type="number"
                            placeholder="Quantity"
                            value={medicine.quantity}
                            onChange={(e) => updateMedicineField(index, 'quantity', e.target.value)}
                            style={{
                              padding: '0.75rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '0.9rem',
                              outline: 'none',
                              transition: 'border-color 0.3s ease',
                              width: '100%',
                              boxSizing: 'border-box'
                            }}
                            onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                            onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                          />
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280' }}>Composition</label>
                        <input
                          type="text"
                          placeholder="Composition"
                          value={medicine.medicine_composition}
                          onChange={(e) => updateMedicineField(index, 'medicine_composition', e.target.value)}
                          style={{
                            padding: '0.75rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            outline: 'none',
                            transition: 'border-color 0.3s ease',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280' }}>Preferred Brand</label>
                        <input
                          type="text"
                          placeholder="Preferred Brand"
                          value={medicine.preferred_brand}
                          onChange={(e) => updateMedicineField(index, 'preferred_brand', e.target.value)}
                          style={{
                            padding: '0.75rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            outline: 'none',
                            transition: 'border-color 0.3s ease',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280' }}>Notes</label>
                        <input
                          type="text"
                          placeholder="Notes"
                          value={medicine.notes}
                          onChange={(e) => updateMedicineField(index, 'notes', e.target.value)}
                          style={{
                            padding: '0.75rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            outline: 'none',
                            transition: 'border-color 0.3s ease',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => removeMedicineRow(index)}
                      disabled={requestMedicines.length === 1}
                      style={{
                        background: requestMedicines.length === 1 
                          ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                          : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '0.75rem',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        cursor: requestMedicines.length === 1 ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        opacity: requestMedicines.length === 1 ? 0.7 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        width: '100%',
                        marginTop: '0.5rem'
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12"/>
                      </svg>
                      Remove
                    </button>
                  </>
                ) : (
                  <>
                    <input
                      type="text"
                      placeholder="Medicine name"
                      value={medicine.medicine_name}
                      onChange={(e) => updateMedicineField(index, 'medicine_name', e.target.value)}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '0.9rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                    />
                    <input
                      type="text"
                      placeholder="Company"
                      value={medicine.medicine_company}
                      onChange={(e) => updateMedicineField(index, 'medicine_company', e.target.value)}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '0.9rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                    />
                    <input
                      type="text"
                      placeholder="Composition"
                      value={medicine.medicine_composition}
                      onChange={(e) => updateMedicineField(index, 'medicine_composition', e.target.value)}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '0.9rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                    />
                    <input
                      type="number"
                      placeholder="Quantity"
                      value={medicine.quantity}
                      onChange={(e) => updateMedicineField(index, 'quantity', e.target.value)}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '0.9rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                    />
                    <input
                      type="text"
                      placeholder="Preferred Brand"
                      value={medicine.preferred_brand}
                      onChange={(e) => updateMedicineField(index, 'preferred_brand', e.target.value)}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '0.9rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                    />
                    <input
                      type="text"
                      placeholder="Notes"
                      value={medicine.notes}
                      onChange={(e) => updateMedicineField(index, 'notes', e.target.value)}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '0.9rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                    />
                    <button
                      onClick={() => removeMedicineRow(index)}
                      disabled={requestMedicines.length === 1}
                      style={{
                        background: requestMedicines.length === 1 
                          ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                          : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem',
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        cursor: requestMedicines.length === 1 ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        opacity: requestMedicines.length === 1 ? 0.7 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12"/>
                      </svg>
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Extra Fields Section */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <h3 style={{
              fontSize: '1.2rem',
              fontWeight: '600',
              color: '#1f2937',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10,9 9,9 8,9"/>
              </svg>
              Extra Fields ({extraFields.length})
            </h3>
            <button
              onClick={addExtraField}
              style={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 2px 6px rgba(245, 158, 11, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 10px rgba(245, 158, 11, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(245, 158, 11, 0.3)';
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M5 12h14"/>
              </svg>
              Add Field
            </button>
          </div>

          {/* Extra Fields */}
          {extraFields.length > 0 && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              maxHeight: '200px',
              overflow: 'auto',
              padding: '0.5rem'
            }}>
              {extraFields.map((field, index) => (
                <div key={index} style={{
                  display: isMobile ? 'flex' : 'grid',
                  flexDirection: isMobile ? 'column' : 'row',
                  gridTemplateColumns: isMobile ? 'none' : '1fr 1fr 60px',
                  gap: isMobile ? '0.75rem' : '0.5rem',
                  padding: isMobile ? '1rem' : '0.75rem',
                  background: 'white',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}>
                  <input
                    type="text"
                    placeholder="Field name"
                    value={field.field_name}
                    onChange={(e) => updateExtraField(index, 'field_name', e.target.value)}
                    style={{
                      padding: isMobile ? '0.75rem' : '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      width: isMobile ? '100%' : 'auto',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#f59e0b'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                  />
                  <input
                    type="text"
                    placeholder="Field value"
                    value={field.field_value}
                    onChange={(e) => updateExtraField(index, 'field_value', e.target.value)}
                    style={{
                      padding: isMobile ? '0.75rem' : '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      width: isMobile ? '100%' : 'auto',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#f59e0b'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                  />
                  <button
                    onClick={() => removeExtraField(index)}
                    style={{
                      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      color: 'white',
                      border: 'none',
                      padding: isMobile ? '0.75rem' : '0.5rem',
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: isMobile ? '100%' : 'auto',
                      gap: isMobile ? '0.5rem' : '0'
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6L6 18M6 6l12 12"/>
                    </svg>
                    {isMobile && <span>Remove</span>}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modal Actions */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: isMobile ? 'stretch' : 'flex-end',
          gap: isMobile ? '0.75rem' : '1rem',
          paddingTop: '1rem',
          borderTop: '1px solid #e5e7eb'
        }}>
          <button
            onClick={closeRequestModal}
            disabled={isSubmittingRequest}
            style={{
              background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
              color: 'white',
              border: 'none',
              padding: isMobile ? '0.875rem 1.25rem' : '0.75rem 1.5rem',
              borderRadius: '8px',
              fontSize: isMobile ? '0.875rem' : '0.9rem',
              fontWeight: '600',
              cursor: isSubmittingRequest ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              opacity: isSubmittingRequest ? 0.7 : 1,
              width: isMobile ? '100%' : 'auto'
            }}
          >
            Cancel
          </button>
          <button
            onClick={submitRequestStock}
            disabled={isSubmittingRequest}
            style={{
              background: isSubmittingRequest 
                ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              border: 'none',
              padding: isMobile ? '0.875rem 1.25rem' : '0.75rem 1.5rem',
              borderRadius: '8px',
              fontSize: isMobile ? '0.875rem' : '0.9rem',
              fontWeight: '600',
              cursor: isSubmittingRequest ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
              width: isMobile ? '100%' : 'auto'
            }}
            onMouseEnter={(e) => {
              if (!isSubmittingRequest) {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmittingRequest) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
              }
            }}
          >
            {isSubmittingRequest ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Submitting...
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12l2 2 4-4"/>
                  <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                </svg>
                Submit Request
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        input::placeholder {
          color: #9ca3af !important;
          opacity: 1;
        }
        
        input::-webkit-input-placeholder {
          color: #9ca3af !important;
          opacity: 1;
        }
        
        input::-moz-placeholder {
          color: #9ca3af !important;
          opacity: 1;
        }
        
        input:-ms-input-placeholder {
          color: #9ca3af !important;
          opacity: 1;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default DistributorRequest;

